import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CLAUDE_MODEL_IDS, type RoutineDTO, type RoutineOutputKind, type ScheduleConfig } from "@app-factory/shared";
import { api } from "@/lib/api";
import { DataTable } from "@/design/DataTable";
import { cs } from "@/i18n/cs";
import { Btn, EmptyState, Field, Panel, inputClass } from "@/design/primitives";
import { useRealtimeEvent } from "@/realtime/useRealtime";

const OUTPUT_KINDS: RoutineOutputKind[] = ["TASK", "PROPOSAL", "DECISION", "REPORT"];

/** Barva posledního výstupu dle designu: běží cyan, OK dim/green, alert red. */
export function runOutcome(routine: RoutineDTO): { text: string; color: string } {
  if (routine.status === "PAUSED") return { text: cs.workers.routinePaused, color: "var(--text-tertiary)" };
  const run = routine.lastRun;
  if (!run) return { text: cs.workers.routineNoRuns, color: "var(--text-tertiary)" };
  if (run.status === "RUNNING") return { text: cs.workers.routineRunning, color: "var(--accent)" };
  if (run.status === "FAILED") return { text: `✗ ${run.summary}`, color: "var(--critical)" };
  const hasOutputs = run.outputRefs.length > 0;
  return { text: run.summary, color: hasOutputs ? "var(--text-secondary)" : "var(--positive)" };
}

export function nextRunLabel(routine: RoutineDTO): string {
  if (routine.status === "PAUSED" || !routine.nextRunAt) return "—";
  const at = new Date(routine.nextRunAt);
  if (at.getTime() <= Date.now()) return "teď (due)";
  const today = new Date().toDateString() === at.toDateString();
  const hm = at.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
  if (today) return `dnes ${hm}`;
  const tomorrow = new Date(Date.now() + 86_400_000).toDateString() === at.toDateString();
  return tomorrow ? `zítra ${hm}` : `${at.toLocaleDateString("cs-CZ")} ${hm}`;
}

/** ROUTINES // OPAKOVANÁ PRÁCE — tabulka dle /Design (workers page). */
export function RoutinesPanel({ isOwner }: { isOwner: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routines = useQuery({ queryKey: ["routines"], queryFn: api.routines, refetchInterval: 60_000 });
  const [creating, setCreating] = useState(false);
  useRealtimeEvent((m) => {
    if (m.type === "registry.changed" && m.area === "routines") {
      void queryClient.invalidateQueries({ queryKey: ["routines"] });
    }
  });

  const list = routines.data?.routines ?? [];

  return (
    <Panel className="mt-4 p-4" data-testid="routines-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="af-label" style={{ color: "var(--accent)" }}>
          {cs.workers.routines}
        </span>
        {isOwner ? (
          <Btn onClick={() => setCreating(true)} data-testid="routine-new">
            {cs.workers.newRoutine}
          </Btn>
        ) : null}
      </div>
      <div className="mb-3 mt-1 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.workers.routinesNote}
      </div>

      {creating ? <RoutineForm onClose={() => setCreating(false)} /> : null}

      {list.length === 0 && !creating ? (
        <EmptyState title={cs.workers.routinesEmpty} />
      ) : (
        <DataTable
            columns={[
              {
                key: "name",
                label: cs.workers.routineCol,
                width: "minmax(160px, 1.2fr)",
                primary: true,
                cell: (r) => r.name,
              },
              {
                key: "dept",
                label: cs.workers.routineDept,
                width: "110px",
                cell: (r) => (
                  <span className="af-mono text-[10px]" style={{ color: "var(--accent)" }}>
                    {(r.departmentName ?? "—").toUpperCase()}
                  </span>
                ),
              },
              {
                key: "scope",
                label: cs.workers.routineScope,
                width: "minmax(140px, 1fr)",
                cell: (r) =>
                  r.projects.length
                    ? r.projects.map((p) => p.name).join(" · ")
                    : cs.workers.routineAllProjects,
              },
              {
                key: "schedule",
                label: cs.workers.routineSchedule,
                width: "120px",
                cell: (r) => r.scheduleLabel,
              },
              {
                key: "outcome",
                label: cs.workers.routineLastOut,
                width: "minmax(150px, 1.3fr)",
                cell: (r) => {
                  const outcome = runOutcome(r);
                  return <span style={{ color: outcome.color }}>{outcome.text}</span>;
                },
              },
              {
                key: "next",
                label: cs.workers.routineNext,
                width: "110px",
                cell: (r) => nextRunLabel(r),
              },
            ]}
            rows={list}
            rowKey={(r) => r.id}
            rowTestId={() => "routine-row"}
            onRowClick={(r) => void navigate({ to: "/routines/$id", params: { id: r.id } })}
            stackBelow={1024}
          />
        )}
    </Panel>
  );
}

/** Formulář nové routine (spec §38: schedule, dept, scope, instrukce, autonomie, outputs). */
function RoutineForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.departments });
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const [name, setName] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [kind, setKind] = useState<ScheduleConfig["kind"]>("DAILY");
  const [minutes, setMinutes] = useState("30");
  const [time, setTime] = useState("07:00");
  const [weekday, setWeekday] = useState("1");
  const [instructions, setInstructions] = useState("");
  const [autonomy, setAutonomy] = useState("");
  const [outputs, setOutputs] = useState<RoutineOutputKind[]>(["TASK", "PROPOSAL"]);
  const [capabilities, setCapabilities] = useState("");
  const [modelHint, setModelHint] = useState("");
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const schedule: ScheduleConfig =
    kind === "INTERVAL"
      ? { kind, minutes: Math.max(1, Number(minutes) || 30) }
      : kind === "DAILY"
        ? { kind, time }
        : { kind, weekday: Number(weekday), time };

  const mutation = useMutation({
    mutationFn: () =>
      api.createRoutine({
        name,
        departmentId: departmentId || null,
        schedule,
        instructions,
        autonomy,
        outputs,
        capabilities: capabilities.split(",").map((s) => s.trim()).filter(Boolean),
        modelHint: modelHint.trim() || null,
        projectIds,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["routines"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  const toggle = <T,>(list: T[], value: T, set: (v: T[]) => void) =>
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  return (
    <Panel brackets className="mb-3 p-4" data-testid="routine-form">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr]">
        <Field label={cs.routine.name}>
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Daily project review" autoFocus data-testid="routine-name" />
        </Field>
        <Field label={cs.routine.department}>
          <select className={inputClass} style={{ background: "var(--surface-inset)" }} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">—</option>
            {(departments.data?.departments ?? [])
              .filter((d) => d.enabled)
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label={cs.routine.scheduleKind}>
          <select
            className={inputClass}
            style={{ background: "var(--surface-inset)" }}
            value={kind}
            onChange={(e) => setKind(e.target.value as ScheduleConfig["kind"])}
            data-testid="routine-kind"
          >
            <option value="INTERVAL">{cs.routine.kindInterval}</option>
            <option value="DAILY">{cs.routine.kindDaily}</option>
            <option value="WEEKLY">{cs.routine.kindWeekly}</option>
          </select>
        </Field>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {kind === "INTERVAL" ? (
          <Field label={cs.routine.minutes}>
            <input className={inputClass} value={minutes} onChange={(e) => setMinutes(e.target.value)} inputMode="numeric" />
          </Field>
        ) : (
          <Field label={cs.routine.time}>
            <input className={inputClass} value={time} onChange={(e) => setTime(e.target.value)} placeholder="07:00" data-testid="routine-time" />
          </Field>
        )}
        {kind === "WEEKLY" ? (
          <Field label={cs.routine.weekday}>
            <select className={inputClass} style={{ background: "var(--surface-inset)" }} value={weekday} onChange={(e) => setWeekday(e.target.value)}>
              {cs.routine.weekdays.map((d, i) => (
                <option key={i} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label={cs.routine.capabilitiesField}>
          <input className={inputClass} value={capabilities} onChange={(e) => setCapabilities(e.target.value)} placeholder="code, research" />
        </Field>
        <Field label={cs.routine.modelField}>
          <select
            className={inputClass}
            style={{ background: "var(--surface-inset)" }}
            value={modelHint}
            onChange={(e) => setModelHint(e.target.value)}
          >
            <option value="">{cs.departments.modelDefault}</option>
            {CLAUDE_MODEL_IDS.map((id) => (
              <option key={id} value={id}>
                {cs.models[id]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-3">
        <Field label={cs.routine.instructionsField}>
          <textarea className={inputClass} rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="routine-instructions" />
        </Field>
      </div>
      <div className="mt-3">
        <Field label={cs.routine.autonomyField}>
          <input className={inputClass} value={autonomy} onChange={(e) => setAutonomy(e.target.value)} placeholder="AUTO: čtení + report — PROPOSAL: změny — DENIED: přímé zásahy" />
        </Field>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <div className="af-label-dim mb-1.5">{cs.routine.outputsField}</div>
          <div className="flex flex-wrap gap-1.5">
            {OUTPUT_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => toggle(outputs, k, setOutputs)}
                className="cursor-pointer px-2 py-1 text-[11px] tracking-[0.08em]"
                style={{ borderRadius: "var(--radius-box)",
                  border: `1px solid ${outputs.includes(k) ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                  color: outputs.includes(k) ? "var(--accent)" : "var(--text-secondary)",
                }}
                data-testid={`routine-output-${k}`}
              >
                {k}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="af-label-dim mb-1.5">{cs.routine.scopeField}</div>
          <div className="flex flex-wrap gap-1.5">
            {(projects.data?.projects ?? []).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(projectIds, p.id, setProjectIds)}
                className="cursor-pointer px-2 py-1 text-[11px]"
                style={{ borderRadius: "var(--radius-box)",
                  border: `1px solid ${projectIds.includes(p.id) ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                  color: projectIds.includes(p.id) ? "var(--accent)" : "var(--text-secondary)",
                }}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Btn onClick={onClose}>{cs.common.cancel}</Btn>
        <Btn
          variant="primary"
          disabled={name.trim().length < 2 || instructions.trim().length === 0 || outputs.length === 0 || mutation.isPending}
          onClick={() => mutation.mutate()}
          data-testid="routine-save"
        >
          {cs.common.create}
        </Btn>
      </div>
    </Panel>
  );
}
